/**
 * Utility functions for scanning directories and loading glass type data from subdirectories
 */

import { fetchAndParseCSV } from './csvUtils';
import { GlassType } from '../types/glassTypes';

/**
 * Scans the /public/glass_pricing/ directory for subdirectories
 * @returns Promise resolving to an array of subdirectory names
 */
export const scanGlassPricingDirectories = async (): Promise<string[]> => {
  try {
    // In a browser environment, we can't directly access the file system
    // Instead, we'll make a fetch request to a special endpoint that returns directory info
    const response = await fetch('/api/glass-pricing-directories');
    
    if (!response.ok) {
      // If the API endpoint doesn't exist, we'll use a fallback approach
      return await scanDirectoriesFallback();
    }
    
    const data = await response.json();
    return data.directories || [];
  } catch (error) {
    console.error('Error scanning glass pricing directories:', error);
    return await scanDirectoriesFallback();
  }
};

/**
 * Fallback method to detect directories by attempting to load a known file
 * @returns Promise resolving to an array of detected subdirectory names
 */
const scanDirectoriesFallback = async (): Promise<string[]> => {
  // List of known or potential subdirectories to check
  const potentialDirs = ['whole'];
  const confirmedDirs: string[] = [];
  
  // For each potential directory, try to fetch a file to confirm it exists
  for (const dir of potentialDirs) {
    try {
      // Try to fetch a file from the directory
      const testResponse = await fetch(`/glass_pricing/${dir}/test-exists.txt`, { method: 'HEAD' });
      
      // If we get a 404, the directory might still exist but the test file doesn't
      // If we get a 200, the directory definitely exists
      if (testResponse.ok) {
        confirmedDirs.push(dir);
        continue;
      }
      
      // Alternative check: try to list the directory contents
      const filesResponse = await fetch(`/glass_pricing/${dir}/`);
      if (filesResponse.ok) {
        confirmedDirs.push(dir);
      }
    } catch (error) {
      // Ignore errors, just means we couldn't confirm this directory
      console.log(`Could not confirm directory ${dir} exists:`, error);
    }
  }
  
  // Add the 'whole' directory if we know it exists from the file structure
  if (!confirmedDirs.includes('whole')) {
    confirmedDirs.push('whole');
  }
  
  return confirmedDirs;
};

/**
 * Loads glass types from a specific subdirectory
 * @param directory Subdirectory name
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
export const loadGlassTypesFromDirectory = async (directory: string, source: string): Promise<GlassType[]> => {
  try {
    // First, try to fetch a list of CSV files in the directory
    const filesResponse = await fetch(`/api/glass-pricing-files?directory=${directory}`);
    
    if (filesResponse.ok) {
      // If the API endpoint exists, use it to get the file list
      const data = await filesResponse.json();
      const files = data.files || [];
      
      // Load each CSV file and combine the results
      const allTypesPromises = files.map(async (file: string) => {
        try {
          const url = `/glass_pricing/${directory}/${file}`;
          return await fetchAndParseCSV(url, source);
        } catch (error) {
          console.error(`Error loading glass types from ${directory}/${file}:`, error);
          return [];
        }
      });
      
      const allTypesArrays = await Promise.all(allTypesPromises);
      return allTypesArrays.flat();
    } else {
      // Fallback: try to load known files
      return await loadGlassTypesFromDirectoryFallback(directory, source);
    }
  } catch (error) {
    console.error(`Error loading glass types from directory ${directory}:`, error);
    return await loadGlassTypesFromDirectoryFallback(directory, source);
  }
};

/**
 * Fallback method to load glass types from a specific subdirectory
 * @param directory Subdirectory name
 * @param source Source identifier (e.g., 'CLG', 'OldCastle')
 * @returns Promise resolving to an array of GlassType objects
 */
const loadGlassTypesFromDirectoryFallback = async (directory: string, source: string): Promise<GlassType[]> => {
  try {
    // For the 'whole' directory, we know the file naming convention
    if (directory === 'whole') {
      const sourceLower = source.toLowerCase();
      const url = `/glass_pricing/whole/${sourceLower}.csv`;
      
      console.log(`Loading glass types from ${url}`);
      return await fetchAndParseCSV(url, source);
    }
    
    // For other directories, we'll need to try common file patterns
    const commonFileNames = [
      `${source.toLowerCase()}.csv`,
      `${source}.csv`,
      'glass_types.csv',
      'prices.csv'
    ];
    
    // Try each potential file name
    for (const fileName of commonFileNames) {
      try {
        const url = `/glass_pricing/${directory}/${fileName}`;
        const response = await fetch(url, { method: 'HEAD' });
        
        if (response.ok) {
          console.log(`Found file ${url}, loading glass types`);
          return await fetchAndParseCSV(url, source);
        }
      } catch (error) {
        // Ignore errors, just means this file doesn't exist
      }
    }
    
    console.warn(`Could not find any glass type files in directory ${directory}`);
    return [];
  } catch (error) {
    console.error(`Error in fallback loading from directory ${directory}:`, error);
    return [];
  }
};
